/**
 * =============================================================================
 * IDS CRM Web - Check Form Preview (Read-Only)
 * =============================================================================
 *
 * PURPOSE:
 * Read-only preview renderer for check form templates.
 * Shows how a template would render as a form without saving data.
 *
 * =============================================================================
 */

import React from 'react';

const SYSTEM_FIELD_TYPES = ['vehicle_select', 'seat_select', 'signature'];

function CheckFormPreview({ schema }) {
  if (!schema?.sections) {
    return (
      <div className="preview-empty">
        <p>No form schema available to preview.</p>
      </div>
    );
  }

  const renderFieldPreview = (field, isFollowUp = false) => {
    const isSystemField = field.system_field || SYSTEM_FIELD_TYPES.includes(field.field_type);

    return (
      <div
        key={field.field_key}
        className={`preview-field ${isFollowUp ? 'follow-up' : ''} ${isSystemField ? 'system' : ''}`}
      >
        <div className="preview-field-label">
          <span className="label-text">{field.label}</span>
          {field.required_on_complete && <span className="required-marker">*</span>}
          {field.allow_photos && (
            <span className="photo-indicator" title="Photo upload enabled">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21,15 16,10 5,21"/>
              </svg>
            </span>
          )}
        </div>

        {isSystemField ? (
          <div className="preview-system-field">
            <span className="system-placeholder">
              {field.field_type === 'vehicle_select' && 'Vehicle selection handled by workflow'}
              {field.field_type === 'seat_select' && 'Seat selection handled by workflow'}
              {field.field_type === 'signature' && 'Signature capture handled by workflow'}
              {!SYSTEM_FIELD_TYPES.includes(field.field_type) && 'System field handled by workflow'}
            </span>
          </div>
        ) : (
          renderFieldInput(field)
        )}

        {field.allow_photos && !isSystemField && (
          <div className="preview-photo-placeholder">
            <span className="photo-placeholder-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                <circle cx="8.5" cy="8.5" r="1.5"/>
                <polyline points="21,15 16,10 5,21"/>
              </svg>
            </span>
            <span className="photo-placeholder-text">+ Photo</span>
          </div>
        )}
      </div>
    );
  };

  const renderFieldInput = (field) => {
    switch (field.field_type) {
      case 'yes_no':
        return (
          <div className="preview-input-group">
            <select disabled className="preview-select">
              <option value="">Select...</option>
              <option value="true">{field.inverted_logic ? 'Yes' : 'Yes'}</option>
              <option value="false">{field.inverted_logic ? 'No (OK)' : 'No'}</option>
            </select>
            {field.inverted_logic && (
              <span className="inverted-hint">Inverted: No = Pass</span>
            )}
          </div>
        );

      case 'yes_no_na':
        return (
          <div className="preview-input-group">
            <select disabled className="preview-select">
              <option value="">Select...</option>
              <option value="true">Yes</option>
              <option value="false">No</option>
              <option value="na">N/A</option>
            </select>
          </div>
        );

      case 'text':
        return (
          <div className="preview-input-group">
            <textarea
              disabled
              className="preview-textarea"
              placeholder={field.placeholder || 'Enter text...'}
              rows={2}
            />
          </div>
        );

      case 'number':
        return (
          <div className="preview-input-group">
            <input
              type="number"
              disabled
              className="preview-input"
              placeholder={field.placeholder || 'Enter number...'}
            />
          </div>
        );

      case 'checkbox':
        return (
          <div className="preview-input-group checkbox">
            <label className="preview-checkbox-label">
              <input type="checkbox" disabled />
              <span className="checkbox-text">{field.label}</span>
            </label>
          </div>
        );

      default:
        return (
          <div className="preview-input-group">
            <input
              type="text"
              disabled
              className="preview-input"
              placeholder={`${field.field_type} field`}
            />
          </div>
        );
    }
  };

  const renderFollowUp = (field) => {
    if (!field.follow_up?.field) return null;

    const conditionLabel = field.follow_up.condition === 'true' ? 'Yes'
      : field.follow_up.condition === 'false' ? 'No'
      : field.follow_up.condition;

    return (
      <div className="preview-follow-up">
        <div className="follow-up-indicator">
          <span className="follow-up-arrow">&#8627;</span>
          <span className="follow-up-condition">When "{conditionLabel}" is selected:</span>
        </div>
        {renderFieldPreview(field.follow_up.field, true)}
      </div>
    );
  };

  return (
    <div className="check-form-preview">
      <div className="preview-header">
        <h4>{schema.name || 'Form Preview'}</h4>
        {schema.description && (
          <p className="preview-description">{schema.description}</p>
        )}
      </div>

      <div className="preview-notice">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="12" y1="16" x2="12" y2="12"/>
          <line x1="12" y1="8" x2="12.01" y2="8"/>
        </svg>
        <span>This is a read-only preview. No data will be saved.</span>
      </div>

      <div className="preview-form">
        {schema.sections
          .filter(section => !section.archived)
          .map((section) => (
          <div key={section.section_key} className="preview-section">
            <div className="preview-section-header">
              <h5>{section.title}</h5>
            </div>
            <div className="preview-section-fields">
              {section.fields?.filter(field => !field.archived).map((field) => (
                <React.Fragment key={field.field_key}>
                  {renderFieldPreview(field)}
                  {renderFollowUp(field)}
                </React.Fragment>
              ))}
            </div>
          </div>
        ))}
        {schema.sections.filter(s => !s.archived).length === 0 && (
          <div className="preview-empty-sections">
            <p>No active sections to preview.</p>
          </div>
        )}
      </div>

      <div className="preview-footer">
        <p>Fields marked with * are required for completion.</p>
      </div>
    </div>
  );
}

export default CheckFormPreview;
